// The brief the Custom HTML/CSS editor hands to an LLM.
//
// An author writes their slide content into a chat, pastes this file alongside
// it, and gets back two blocks they can drop straight into the modal's two
// fields. So the brief has to state every constraint the block actually
// enforces — the tag allowlist, the CSS scoping, the 16:9 geometry, the
// background rules — and it has to stay true as those change. Everything below
// is therefore GENERATED FROM THE REAL CONSTANTS: the allowlists come from
// sanitizeHtml.ts, the CSS rules from customCss.js, the slide geometry from
// slideFrames.js. Edit the rule, and the brief follows.

import {
  SLIDE_LAYOUT_TAGS,
  SLIDE_LAYOUT_ATTR,
  SLIDE_LAYOUT_FORBIDDEN_TAGS,
} from './sanitizeHtml';
import { CSS_STRIP_RULES, customHtmlScopeSelector } from './customCss';
import {
  buildMediaNote,
  formatInText,
  formatReference,
  mapCitationToSourceMeta,
} from '../lib/apaSlideFormatter';
import {
  EXPORT_SLIDE_W,
  EXPORT_SLIDE_H,
  EXPORT_HEADER_H,
  EXPORT_CONTENT_H,
} from './slideFrames';

const CSS_PX_PER_IN = 96;

const round = (value) => Math.round(value * 100) / 100;

/** The content region: the slide minus its header bar. */
export const SLIDE_GEOMETRY = {
  slideWidthIn: round(EXPORT_SLIDE_W),
  slideHeightIn: round(EXPORT_SLIDE_H),
  headerHeightIn: round(EXPORT_HEADER_H),
  contentHeightIn: round(EXPORT_CONTENT_H),
  contentWidthPx: Math.round(EXPORT_SLIDE_W * CSS_PX_PER_IN),
  contentHeightPx: Math.round(EXPORT_CONTENT_H * CSS_PX_PER_IN),
  contentAspect: round(EXPORT_SLIDE_W / EXPORT_CONTENT_H),
};

const aspectLabel = (width, height) => {
  if (!width || !height) return '—';
  const ratio = width / height;
  if (Math.abs(ratio - 1) < 0.04) return 'square';
  if (Math.abs(ratio - 16 / 9) < 0.06) return 'landscape 16:9';
  if (Math.abs(ratio - 4 / 3) < 0.06) return 'landscape 4:3';
  if (Math.abs(ratio - 3 / 2) < 0.06) return 'landscape 3:2';
  if (Math.abs(ratio - 9 / 16) < 0.06) return 'portrait 9:16';
  if (Math.abs(ratio - 3 / 4) < 0.06) return 'portrait 3:4';
  return `${ratio > 1 ? 'landscape' : 'portrait'} ${Math.round(ratio * 100) / 100}:1`;
};

/** The uploads, as a table the assistant can design around. */
const imageTable = (images) => {
  const rows = images.map((image, index) => {
    const name = String(image?.name || `image-${index + 1}`);
    const w = Number(image?.width) || 0;
    const h = Number(image?.height) || 0;
    const size = w && h ? `${w} x ${h} px` : 'size unknown';
    const alt = String(image?.alt || '').trim() || '—';
    return `| \`${name}\` | ${size} | ${aspectLabel(w, h)} | ${alt} | \`var(--lp-${name})\` |`;
  });
  return [
    '| Reference | Pixels | Shape | What it shows | CSS variable |',
    '|---|---|---|---|---|',
    ...rows,
  ].join('\n');
};

/**
 * The note an author has to place in the layout for each source they cited.
 *
 * The same rule the block's editor previews: pictures and tables take their
 * "Note." form, everything else takes the parenthetical in-text citation.
 */
export const citationNote = (citation) => {
  const meta = mapCitationToSourceMeta(citation);
  const note = buildMediaNote(meta);
  // The brief is plain text an author copies into a layout, so a linked licence
  // travels as its deed URL rather than being lost.
  if (note) return note.plainText;
  return formatInText(meta);
};

/** Everything known about one source, for the "what is this" table. */
const citationFacts = (citation) => {
  const meta = mapCitationToSourceMeta(citation);
  return {
    note: String(citationNote(citation) || '').trim(),
    reference: String(formatReference(meta) || '').trim(),
    kind: meta.sourceType || '',
    title: meta.title,
    author: meta.author,
    when: meta.date || meta.year || '',
    url: meta.doi ? `https://doi.org/${meta.doi}` : meta.url,
  };
};

const citationSection = (citations) => {
  const sources = citations.map(citationFacts).filter((source) => source.note);
  if (!sources.length) return '';

  const one = sources.length === 1;
  const details = sources.map((source, index) => {
    const lines = [
      `**${index + 1}. ${source.title || 'Untitled source'}**`,
      source.author ? `- Author / channel: ${source.author}` : '',
      source.when ? `- Published: ${source.when}` : '',
      source.kind ? `- Kind: ${source.kind}` : '',
      source.url ? `- Link: ${source.url}` : '',
      source.reference ? `- Full reference (the app renders this, you must not): ${source.reference}` : '',
      `- **In-text note to place in the layout:** ${source.note}`,
    ];
    return lines.filter(Boolean).join('\n');
  }).join('\n\n');

  return `## Citations

The author has cited ${one ? 'a source' : 'these sources'} for this slide. The details are here so you
know what the slide is drawing on and can write copy that refers to it
accurately — mention the title or the channel in the body text where it helps.

${details}

Place the in-text note${one ? '' : 's'} in the layout — under the figure ${one ? 'it belongs' : 'they belong'} to, or in a
small footnote line at the bottom — styled quietly (around 1.1cqw, reduced
opacity) so it reads as attribution rather than content. Reproduce each note
**exactly** as written.

Do NOT render the full reference entry and do NOT add a "References" heading:
the app builds the deck's reference list from these sources by itself, on its
own slide at the end. The full entries are shown above only so you can see what
the reader will eventually get.

`;
};

const list = (items, perLine = 8) => {
  const lines = [];
  for (let i = 0; i < items.length; i += perLine) {
    lines.push(items.slice(i, i + perLine).join(', '));
  }
  return lines.map((line) => `  ${line}`).join('\n');
};

/** A 1x1 transparent PNG: a placeholder that survives the sanitizer. */
const TRANSPARENT_PIXEL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';

/**
 * The skeleton HTML. Structure only — every string is a placeholder label, so
 * the author's own content replaces it rather than being blended with sample
 * copy that then has to be picked back out.
 */
export const EXAMPLE_HTML = `<!-- One root element. Everything else nests inside it. -->
<section class="lp-slide" data-layout="two-column">

  <!-- Optional: the slide's own heading. The coloured header bar above the
       slide already carries the slide title, so use this for a lead-in
       rather than repeating that title. -->
  <header class="lp-slide__head">
    <h2 class="lp-slide__title">SLIDE HEADING</h2>
    <p class="lp-slide__lede">One-line lead-in.</p>
  </header>

  <!-- The body. Columns, cards, lists — whatever the content needs. -->
  <div class="lp-slide__body">

    <div class="lp-col lp-col--main">
      <h3 class="lp-col__title">COLUMN HEADING</h3>
      <p>Body copy.</p>
      <ul class="lp-list">
        <li>Point one.</li>
        <li>Point two.</li>
      </ul>
    </div>

    <div class="lp-col lp-col--aside">
      <!-- Preferred: reference an uploaded picture by its slot name and write
           no src — the app fills in the picture, its width and its height.
           Use the names listed in the brief; do not invent one. -->
      <figure class="lp-figure">
        <img data-lp-image="image-1" alt="Describe the image.">
        <figcaption>Note. Caption. From Author, Year.</figcaption>
      </figure>

      <!-- Only if there is no upload to reference: a real data: or https URL.
           The 1x1 transparent PNG below is a stand-in, not a picture. -->
      <!-- <img src="${TRANSPARENT_PIXEL}" alt="Describe the image."> -->
    </div>

  </div>

  <!-- Optional: a footer strip for a takeaway, source line, or next step. -->
  <footer class="lp-slide__foot">
    <p>Closing line.</p>
  </footer>

</section>`;

/**
 * The skeleton CSS. Every rule here exists to demonstrate one of the
 * constraints above it — the fit-to-slide box, the transparent root, the
 * container-relative type scale.
 */
export const EXAMPLE_CSS = `/* ---------------------------------------------------------------
   Root. Sized to the slide's content region, never taller.
   Transparent on purpose: the slide's background image lives on an
   ancestor, and an opaque background here would hide it.
   --------------------------------------------------------------- */
.lp-slide {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1.5%;

  width: 100%;
  /* The content region is ${SLIDE_GEOMETRY.contentAspect}:1. Holding that ratio keeps the
     layout identical in the editor, the HTML export and PowerPoint. */
  aspect-ratio: ${SLIDE_GEOMETRY.contentWidthPx} / ${SLIDE_GEOMETRY.contentHeightPx};
  max-height: 100%;
  overflow: hidden;   /* Nothing spills off the slide. */

  padding: 3% 4%;
  background: transparent;
  color: inherit;     /* Inherit the deck's text colour. */
}

.lp-slide *,
.lp-slide *::before,
.lp-slide *::after {
  box-sizing: inherit;
}

/* ---------------------------------------------------------------
   Type. Sized in cqw — 1cqw is 1% of this layout's own width — so
   everything scales together instead of breaking when the deck is
   presented full screen.
   --------------------------------------------------------------- */
/* font-weight is spelled out on every heading on purpose — the editor's CSS
   reset sets h1-h6 to inherit both size and weight, so a heading you leave to
   the browser default renders bold in the exported file and NOT bold in the
   editor. Declare it and the two agree. */
.lp-slide__title {
  font-size: 3.4cqw;
  font-weight: 700;
  line-height: 1.15;
  margin: 0;
}

.lp-slide__lede {
  font-size: 1.6cqw;
  font-weight: 400;
  opacity: 0.75;
  margin: 0.4em 0 0;
}

.lp-slide__body {
  flex: 1 1 auto;
  min-height: 0;      /* Lets a flex child actually shrink. */
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 2.5%;
  font-size: 1.5cqw;
}

.lp-col {
  min-width: 0;
  min-height: 0;
}

.lp-col__title {
  font-size: 2cqw;
  font-weight: 600;
  margin: 0 0 0.5em;
}

/* Same reason: the reset strips list markers, so state the marker or the
   bullets vanish in the editor while surviving in the export. */
.lp-list {
  list-style: disc outside;
  margin: 0.6em 0 0;
  padding-left: 1.2em;
  line-height: 1.5;
}

/* ---------------------------------------------------------------
   Images. Fit the box rather than setting the box.
   --------------------------------------------------------------- */
.lp-figure {
  margin: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.lp-figure img {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  object-fit: cover;
  /* cqw, not rem: a radius pinned to an absolute length is the one thing that
     does not grow with the slide, so the same corner reads squarer at one
     scale and rounder at another. */
  border-radius: 0.6cqw;
}

.lp-figure figcaption {
  font-size: 1.1cqw;
  opacity: 0.75;
  margin-top: 0.5em;
}

/* ---------------------------------------------------------------
   Panels. If you need contrast over a background image, tint a
   panel rather than painting the root opaque.
   --------------------------------------------------------------- */
.lp-col--aside {
  background: rgb(255 255 255 / 0.72);
  border-radius: 0.9cqw;
  padding: 1.5%;
}

.lp-slide__foot {
  font-size: 1.2cqw;
  opacity: 0.8;
}

/* Presented full screen the slide gets much wider; cqw already handles
   the scaling, so reach for a media query only for real reflow. */
@media (max-width: 700px) {
  .lp-slide__body {
    grid-template-columns: 1fr;
  }
}`;

/**
 * Build the whole brief.
 *
 * `blockId` is only used to show the author the exact selector their rules
 * will be prefixed with, so an unsaved block (no id yet) gets a placeholder.
 */
export const buildCustomHtmlBrief = ({ blockId = '', deckScoped = false, images = [], videos = [], citations = [] } = {}) => {
  const scopeSelector = customHtmlScopeSelector(blockId || 'THIS-BLOCK');
  const g = SLIDE_GEOMETRY;
  const uploads = (Array.isArray(images) ? images : []).filter((image) => image && image.src);
  const sources = Array.isArray(citations) ? citations.filter(Boolean) : [];
  const clips = (Array.isArray(videos) ? videos : []).filter((video) => video && video.url);

  return `# Custom HTML/CSS slide layout — build brief

You are writing the body of ONE slide in LessonPlanner One. Read the whole brief
before you start: the app sanitizes what you produce, and anything outside the
rules below is silently removed rather than shown as an error.

## What to give back

Exactly two fenced code blocks, in this order, with nothing else between them:

1. an \`html\` block — goes in the modal's **HTML** field.
2. a \`css\` block — goes in the modal's **CSS** field.

Do NOT wrap the HTML in \`<!doctype>\`, \`<html>\`, \`<head>\` or \`<body>\`; it is a
fragment that renders inside a slide that already exists. Do NOT put a
\`<style>\` tag in the HTML — the CSS belongs in the second block. (If you do,
the app moves it into the stylesheet for you, but the fields stay cleaner if
you don't.)

## The slide

| | |
|---|---|
| Slide | ${g.slideWidthIn}in x ${g.slideHeightIn}in — 16:9 |
| Header bar | ${g.headerHeightIn}in, above your layout, not yours to draw |
| **Your content region** | **${g.slideWidthIn}in x ${g.contentHeightIn}in ≈ ${g.contentWidthPx}px x ${g.contentHeightPx}px, ratio ${g.contentAspect}:1** |

The slide title and its subtitle are already printed in that header bar. Do not
repeat them as the first line of your layout.

### Fit

* The content region is fixed. **Design to fit it — do not design a page that
  scrolls.** Anything past the bottom edge is cut off in the exports.
* Size in **percentages, \`fr\`, \`cqw\` and \`em\`**, not fixed pixels. The same
  markup is rendered at editor scale, at full-screen presentation scale, and at
  export scale; fixed pixel sizes only look right at one of them.
* \`cqw\` works and is the best tool here: your root's wrapper is a CSS
  container, so **\`1cqw\` = 1% of your layout's own width**, and it means the
  same thing in the editor, at presentation scale and in the exported file.
* **This applies to \`border-radius\`, \`border-width\`, \`box-shadow\` and \`gap\`
  too — not just type.** A radius in \`rem\` or \`px\` is the one thing on the
  slide that does not grow when the slide does, so the same box reads squarer
  in the editor and rounder in the export, purely because the two are rendered
  at slightly different widths. Write \`border-radius: 1.2cqw\`, not
  \`border-radius: 1rem\`, and the corner keeps its proportion everywhere.
  \`rem\` and \`px\` are safe only for hairlines you *want* pinned, like a 1px
  rule.
* Give flex/grid children \`min-width: 0\` / \`min-height: 0\` so they shrink
  instead of forcing the layout past the edge.
* Put \`overflow: hidden\` on your root so a long line clips rather than spilling
  over the slide.

### Style everything explicitly — do not rely on browser defaults

The editor page carries a CSS reset that the exported file does not. Anything
you leave to the browser's default styling therefore renders **differently in
the editor than in the export**. Two that bite every time:

* \`h1\`-\`h6\` are reset to \`font-size: inherit; font-weight: inherit\`. Always
  declare \`font-size\` **and** \`font-weight\` on a heading.
* \`ul\`/\`ol\` lose their markers, margin and padding. Always declare
  \`list-style\`, \`margin\` and \`padding-left\` on a list.

The same caution applies to \`blockquote\`, \`table\` borders, \`hr\` and paragraph
margins: if it matters, write it down.

### Background images

A slide (or the whole deck) can carry a background image, set on an ancestor of
your layout.

* **Keep your root transparent.** \`background: transparent\` — an opaque colour
  there hides the deck's background image on every slide that has one.
* Need contrast for text? Tint an inner panel — \`rgb(255 255 255 / 0.72)\` or a
  gradient scrim — rather than painting the root.
* Do not set \`background-image\` on the root. That is the slide's job, and the
  author sets it in Slide Settings.
* \`color: inherit\` on the root keeps your text in the deck's colour scheme.

## HTML rules

Allowed tags — everything else is dropped, its text kept:

${list(SLIDE_LAYOUT_TAGS)}

Allowed attributes:

${list(SLIDE_LAYOUT_ATTR)}

\`data-*\` attributes are allowed, and are the right way to hang a CSS hook on an
element. \`aria-*\` is allowed; use it.

**Removed, always:**

${list(SLIDE_LAYOUT_FORBIDDEN_TAGS)}

So: no scripts, no iframes, no embeds, no form controls, no interactivity beyond
what CSS can do (\`:hover\`, \`<details>\`/\`<summary>\`). \`on*\` handlers and
\`javascript:\` URLs are stripped.

### Images

There are two ways to put a picture on the slide. **Prefer the first.**

**1. An uploaded picture (preferred).** The author uploads images in the block's
editor; each gets a short slot name. Reference one by name and the app fills in
the picture itself:

    <img data-lp-image="image-1" alt="Describe the image.">

Write no \`src\` — the app supplies it, along with the intrinsic \`width\` and
\`height\`. The picture is a real asset, so it exports to PowerPoint and PDF as an
image rather than being lost inside a string, and the HTML export embeds it.
Each upload is also available to your CSS as a custom property, so it can be a
backdrop instead of an \`<img>\`:

    .hero { background-image: var(--lp-image-1); background-size: cover; }

${uploads.length
    ? `**Uploaded and ready to use** — these exist right now; use these names and\nsizes, and do not invent others:\n\n${imageTable(uploads)}\n\nDesign around those proportions: an image is placed with \`object-fit\`, so a\nbox whose aspect ratio is far from the picture's will crop it heavily.`
    : `**Nothing is uploaded yet.** If the layout needs a picture, reference the slot\nnames the author tells you (\`image-1\`, \`image-2\`, …) and state in your reply\nwhat each one should be and roughly what shape it needs to be — they will upload\nto match. Do NOT invent a base64 string.`}

### Videos

${clips.length
    ? `The author has linked ${clips.length === 1 ? 'a clip' : 'these clips'}. Reference one with an empty anchor and
the app replaces it with a clickable poster image:

\`\`\`
<a data-lp-video="${clips[0].name || 'video-1'}"></a>
\`\`\`

${clips.map((clip) => {
      const facts = [clip.title, clip.author, clip.date].map((v) => String(v || '').trim()).filter(Boolean);
      return `- \`${clip.name}\` — ${clip.platform || 'video'}${facts.length ? `: ${facts.join(' · ')}` : ''}`;
    }).join('\n')}

The resolved anchor carries \`class="lp-video"\` with the poster inside it as
\`.lp-video__poster\`, and comes pre-styled as a 16:9 card with a play glyph.
Place it — give it a grid area or a width — and only restyle it if you mean to;
your rules win over the defaults. It is a **link, not a player**: no iframe
survives the sanitizer, and a poster is the one form that works in the editor,
the HTML file, PowerPoint and the PDF alike.`
    : `No clips are linked. If the layout needs one, leave a labelled placeholder
and say so in your reply — the author adds the link in the block's editor and it
resolves to a clickable poster. Do NOT write an \`<iframe>\`: it is stripped.`}

**2. A self-contained URL.** A base64 raster data URL
(\`data:image/png;base64,…\`, or jpeg/gif/webp/avif) or an \`https:\` URL that will
still resolve wherever the deck is opened. Use this only for something already
on the web; never fabricate base64. \`data:image/svg+xml\` is blocked — it is a
script vector, not a picture.

Either way: an \`<img>\` should fit the box the layout gives it
(\`width: 100%; object-fit: cover\`), not set the box's size.

## CSS rules

${deckScoped
  ? `This block is set to **deck scope**: your rules are left global and apply to
every slide in the deck. Namespace every class (\`.lp-…\`) and never style bare
element selectors — \`p { }\` would restyle the entire deck.`
  : `This block is **slide-scoped** (the default). Before the stylesheet is used,
every selector you write is prefixed with:

    ${scopeSelector}

so \`.lp-col { }\` becomes \`${scopeSelector} .lp-col { }\`. Your rules cannot reach
another slide, and the rest of the deck's styling cannot be broken by them.

\`html\`, \`body\` and \`:root\` collapse onto your root element, so \`body { }\` is a
valid way to style it. \`@keyframes\` and \`@font-face\` are passed through
untouched; \`@media\`, \`@supports\`, \`@layer\` and \`@container\` are scoped inside.`}

Removed from the stylesheet:

${CSS_STRIP_RULES.map((rule) => `* ${rule}`).join('\n')}

Web fonts must therefore be system fonts or a font stack — no \`@import\` from
Google Fonts.

${citationSection(sources)}## Structure to follow

The two blocks below are the shape to copy. They are structure only: every
visible string is a placeholder for the author's content, which is supplied
separately. Keep the fit, background and scoping behaviour; change the layout,
the class names and the styling to suit that content.

\`\`\`html
${EXAMPLE_HTML}
\`\`\`

\`\`\`css
${EXAMPLE_CSS}
\`\`\`

## Before you answer

- [ ] Two code blocks, HTML then CSS, nothing else between them.
- [ ] No \`<script>\`, \`<iframe>\`, \`<style>\`, form controls or \`on*\` attributes.
- [ ] One root element wrapping the layout.
- [ ] Root is transparent and does not set a background image.
- [ ] Every heading declares \`font-weight\`; every list declares \`list-style\`.
- [ ] Sized in %, \`fr\`, \`cqw\`, \`em\` — the layout fits ${g.contentAspect}:1 without scrolling.
- [ ] \`border-radius\`, \`border-width\`, \`gap\` and shadows are in \`cqw\` too, so
      corners keep their proportion at every scale.
- [ ] Every class namespaced, so nothing collides with the deck's own styles.
- [ ] Pictures reference an uploaded slot (\`data-lp-image\`) where one exists;
      only a real data: or https URL otherwise, never invented base64.
`;
};

export const CUSTOM_HTML_BRIEF_FILENAME = 'lessonplanner-custom-slide-brief.md';
